/**
 * Fine redlining (7.2): zoom and pan inside the sheet markup editor, on fabric's viewportTransform. Pure helpers (no DOM,
 * no fabric) so the maths is tested on its own. The canvas element keeps its fit-to-width size: zooming changes what part
 * of the page it shows, never the page (canvas) coordinates objects are saved in.
 */

/** + / − steps; a pinch or ctrl + wheel stays between the first and last */
export const MARKUP_ZOOMS: readonly number[] = [1, 1.5, 2, 3, 4, 6, 8];
export const MARKUP_ZOOM_MAX = 8;

/** The thinnest new stroke (canvas px): S at full zoom still shows */
export const STROKE_FLOOR_PX = 0.75;
/** The smallest new text (canvas px) */
export const FONT_FLOOR_PX = 4;

/** fabric's viewportTransform: [zoom, 0, 0, zoom, translateX, translateY] */
export type Viewport = [number, number, number, number, number, number];

export const FIT_VIEWPORT: Viewport = [1, 0, 0, 1, 0, 0];

/**
 * A new stroke's width (or text size) at this zoom: the chosen size divided by the zoom, so it looks the same on screen
 * and lands finer on the page, never below `floor`. Existing objects keep theirs.
 */
export function zoomedSize(base: number, zoom: number, floor = STROKE_FLOOR_PX): number {
  return Math.max(floor, base / Math.max(1, zoom || 1));
}

const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));

/** Keeps the zoomed page covering the canvas: no blank edge while panning, and back to the page's origin at 1× */
export function clampViewport(vpt: Viewport, width: number, height: number): Viewport {
  const zoom = clamp(vpt[0], 1, MARKUP_ZOOM_MAX);
  return [zoom, 0, 0, zoom, clamp(vpt[4], width - width * zoom, 0), clamp(vpt[5], height - height * zoom, 0)];
}

/** Zooms to `zoom` keeping the page point under (x, y) (canvas element px) where it is */
export function zoomViewportAt(vpt: Viewport, zoom: number, x: number, y: number, width: number, height: number): Viewport {
  const next = clamp(zoom, 1, MARKUP_ZOOM_MAX);
  const px = (x - vpt[4]) / vpt[0];
  const py = (y - vpt[5]) / vpt[0];
  return clampViewport([next, 0, 0, next, x - px * next, y - py * next], width, height);
}

/** Moves the view by (dx, dy) screen px, the page following the pointer */
export function panViewport(vpt: Viewport, dx: number, dy: number, width: number, height: number): Viewport {
  return clampViewport([vpt[0], 0, 0, vpt[0], vpt[4] + dx, vpt[5] + dy], width, height);
}

/** A page point (canvas units) as a percentage of the canvas element, after the zoom: where an HTML overlay goes */
export function viewportPercent(vpt: Viewport, x: number, y: number, width: number, height: number): { left: number; top: number } {
  return { left: ((x * vpt[0] + vpt[4]) / width) * 100, top: ((y * vpt[0] + vpt[5]) / height) * 100 };
}

/** The CSS transform that makes a page-sized HTML layer follow the zoom (transform-origin 0 0) */
export function viewportCss(vpt: Viewport): string {
  return vpt[0] === 1 && !vpt[4] && !vpt[5] ? '' : `matrix(${vpt[0]}, 0, 0, ${vpt[0]}, ${vpt[4]}, ${vpt[5]})`;
}
